// Phase 2 — cloud sync read path.
//
// The /transcripts page reads from both the local helper AND the backend's
// /v1/sync/transcripts. Local rows are preferred when an id exists in both;
// sync-only rows are surfaced with a "Cloud" badge so users see them as
// "captured on another device".
//
// Decryption of sync-only ciphertext is intentionally NOT here — the web
// app doesn't have the helper's master key, so we display sync-only rows
// as read-only metadata until the helper exposes a decrypt-on-demand
// route (deferred for Phase 2.1).

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './ApiProvider';
import { useAuth } from '../auth/AuthProvider';
import { env } from '../env';
import { useHelperClient } from './helper-queries';

export interface SyncedTranscriptMeta {
  transcript_id: string;
  ciphertext_size: number;
  encrypted_iv_b64: string;
  content_hash: string;
  blob_object_name: string;
  updated_at_ms: number;
}

export interface SyncedTranscriptListResponse {
  transcripts: SyncedTranscriptMeta[];
}

export const syncKeys = {
  list: ['sync', 'transcripts'] as const,
};

export function useSyncedTranscripts(opts: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: syncKeys.list,
    queryFn: () => api.get<SyncedTranscriptListResponse>('/v1/sync/transcripts'),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useDeleteSyncedTranscript() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/v1/sync/transcripts/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: syncKeys.list }),
  });
}

/**
 * Push the user's Auth0 access token + the backend URL to the helper's
 * /v1/cloud/credentials so its outbound sync uploads have valid creds.
 * Idempotent — call freely; the helper just clobbers the cached row.
 */
export function useSyncCloudCredentialsToHelper() {
  const auth = useAuth();
  const helper = useHelperClient();

  useEffect(() => {
    if (!auth.isAuthenticated || !helper) return;
    let cancelled = false;
    const push = async () => {
      try {
        const token = await auth.getToken();
        if (!token || cancelled) return;
        // Decode the JWT to get the actual expiry; for JWS this is the
        // `exp` claim in unix seconds. Fall back to "1 hour from now"
        // if parsing fails (the helper revalidates on each upload, so
        // an over-estimate just means one extra failed request before
        // the token gets re-pushed).
        const expSecs = jwtExp(token) ?? Math.floor(Date.now() / 1000) + 3600;
        await helper.putCloudCredentials({
          backend_url: env.API_URL,
          access_token: token,
          expires_at_ms: expSecs * 1000,
        });
      } catch {
        // Helper unreachable or unpaired — quietly ignore. The mutation
        // will retry next time auth state changes.
      }
    };
    void push();
    return () => {
      cancelled = true;
    };
    // Re-push when auth identity or helper connection changes.
  }, [auth.isAuthenticated, auth, helper]);
}

function jwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}
